import type { IPublicClientApplication } from '@azure/msal-browser';
import { apiRequest } from '@/auth/msal';
// One-way dependency by design: console-capture.ts deliberately does NOT import
// this module (its acquireBearer is inlined "to keep observability layer-free"),
// so importing it here creates no cycle.
import { autoBugReport, getSessionId } from '@/observability/console-capture';
import { CLIENT_DEVICE_HEADER, clientDeviceHeader } from '@/observability/device-info';

export class HttpError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

/**
 * What a failed response says to a person.
 *
 * ⚠️ NOT `res.statusText`. HTTP/2 removed the reason phrase, so every browser
 * reports it as `''` against a modern server — which made the message the bare
 * string `"404 "`. That is what an operator saw on the AP batch page and filed
 * (preview, bugId 6ff4dd10, 2026-09-15); the server had sent
 * `{"error":"batch not found"}` and the SPA discarded it.
 *
 * Order: the server's own sentence, then a plain-English fallback per status,
 * then the status alone — which no longer reaches a screen because the
 * fallbacks cover every class.
 *
 * The raw body stays on `HttpError.body` either way. The pages that already
 * read it for a richer response (a 409's `current_row`, a conflict list) are
 * unaffected — this only decides the message they fall BACK to.
 */
const STATUS_FALLBACK: Record<number, string> = {
  400: 'The request was not valid.',
  401: 'Your session has expired — sign in again.',
  403: 'You do not have access to this.',
  404: 'Not found.',
  409: 'Someone else changed this first — reload and try again.',
  413: 'That file is too large.',
  429: 'Too many requests — wait a moment and try again.',
  500: 'Something went wrong on the server.',
  502: 'The server is unreachable.',
  503: 'The server is busy or restarting — try again shortly.',
  504: 'The server took too long to answer.',
};

export function describeHttpFailure(status: number, body: unknown): string {
  const sentence = (body as { error?: unknown } | undefined)?.error;
  if (typeof sentence === 'string' && sentence.trim()) return sentence;
  const fallback = STATUS_FALLBACK[status] ?? (status >= 500
    ? 'Something went wrong on the server.'
    : 'The request failed.');
  return `${fallback} (${status})`;
}

/**
 * Acquire an access token for the api scope. Returns null if no account is
 * signed in or `acquireTokenSilent` fails — most often because the session
 * lapsed (expired token / dead Entra session cookie) or, on first sign-in,
 * the user has no app role assignment. A null token means the request goes
 * out unauthenticated and the api answers 401; AppShell turns that 401 into
 * an interactive re-auth rather than a dead-end.
 */
export async function getAccessToken(
  msal: IPublicClientApplication,
): Promise<string | null> {
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msal.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch (err) {
    console.warn('[auth] acquireTokenSilent failed', err);
    return null;
  }
}

/**
 * Fetch wrapper that injects the MSAL bearer token. In dev-bypass mode (no
 * MSAL account) the request goes out unauthenticated — useful for the mock
 * endpoints behind DEV_AUTH_BYPASS.
 */
// The dev-bypass role the shell's picker currently has selected, mirrored
// out of React state so `apiFetch` can send it. Module-scope for the same
// reason `getSessionId()` is: every request needs it, and threading it through
// ~180 call sites to serve a local-only affordance is not worth it.
//
// Sent as `x-dev-auth-roles`, which admin-api honours ONLY under
// DEV_AUTH_BYPASS. Without it the picker changed what the SPA rendered while
// the server still answered as whatever DEV_AUTH_ROLES said at startup — two
// answers to "who am I", so the UI would offer edits a real Viewer is refused.
//
// null in every deployed environment (the picker only renders under bypass),
// so no header is sent and the server ignores it regardless.
// A COMMA-SEPARATED LIST — the picker is multi-select, and the server unions
// the grants of every role named (`reachFor`/`grantsFor` iterate and union, so
// `Admin,Local` reaches everything either reaches). Entra can assign a user
// several app roles, so multi-role is a real production shape, not a dev
// convenience.
let devBypassRoles: string | null = null;

export function setDevBypassRoleHeader(roles: string | null): void {
  devBypassRoles = roles;
}

/**
 * The "view as" subset the role picker currently has selected, mirrored out of
 * React state for the same reason as `devBypassRoles` above: every request
 * needs it, and threading it through ~180 call sites is not worth it.
 *
 * Sent as `x-view-as-roles`, which admin-api INTERSECTS with the roles the
 * verified token already carries — so this can only ever narrow the session,
 * never widen it. Unlike `x-dev-auth-roles` it is honoured in EVERY
 * environment, because it grants nothing: that is the whole point, since the
 * environment it exists for is staging.
 *
 * ⚠️ Not a security control on this side. The browser could send any value; the
 * server's intersection is what makes it safe. Do not add client-side
 * validation here and treat it as enforcement.
 *
 * null = no narrowing, no header sent, today's behaviour.
 */
let viewAsRoles: string | null = null;

export function setViewAsRoleHeader(roles: string | null): void {
  viewAsRoles = roles;
}

export async function apiFetch(
  msal: IPublicClientApplication | null,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = msal ? await getAccessToken(msal) : null;
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  // The SAME id the observability layer stamps on _client_events rows, so a
  // bug report and the API calls around it join on one key. Without it the two
  // timelines can only be correlated by user + timestamp, which is guesswork
  // the moment anything is concurrent.
  //
  // ⚠️ Minted per PAGE LOAD, not per login — `SESSION_ID` is module-scope in
  // console-capture.ts, so a refresh starts a new one. That is the intended
  // grain (Craig, 2026-08-12): it groups one screen's activity, which is what
  // reconstructing a path needs. Do not read it as a login-to-logout session.
  headers.set('x-client-session', getSessionId());
  // Screen geometry + pixel ratio, so a login row can say what the person was
  // signed in on. Read fresh per request because a browser can move between
  // displays mid-session; the server only keeps the first one it sees per
  // token issuance. Absent outside a browser, and never sent empty.
  const device = clientDeviceHeader();
  if (device) headers.set(CLIENT_DEVICE_HEADER, device);
  // Local dev only — see setDevBypassRoleHeader. Roles are FABRICATED; nothing
  // here relaxes enforcement. The old `x-dev-auth-strict: false` escape hatch
  // is gone (2026-08-17): a local session that skipped enforcement could not
  // show a masking or gating bug, which is the class of defect that kept
  // reaching the browser past a green CI.
  if (devBypassRoles) headers.set('x-dev-auth-roles', devBypassRoles);
  // Narrowing only — the server intersects this against the token's own roles,
  // so a tampered value cannot widen the session. Sent in every environment.
  if (viewAsRoles) headers.set('x-view-as-roles', viewAsRoles);
  // Only default to JSON for non-FormData bodies. A FormData body must carry the
  // browser-generated multipart/form-data content-type (with its boundary) — set
  // it to application/json and the server rejects the upload (FST_ERR_CTP_INVALID_JSON_BODY).
  if (init.body && !headers.has('content-type') && !(init.body instanceof FormData)) {
    headers.set('content-type', 'application/json');
  }
  return fetch(path, { ...init, headers });
}

/**
 * A failed api call becomes a bug report HERE, at the network, rather than in
 * react-query's `onError`.
 *
 * ⚠️ The capture used to be wired to the LIBRARY (`main.tsx`'s QueryCache /
 * MutationCache `onError`), so any caller that awaited `apiJson` itself and
 * handled the failure in its own try/catch was invisible. That is not an edge
 * case: the reports page runs and downloads every report that way
 * (`pages/reports/reports-page.tsx` — `onRun`, `onDownloadCsv`), each with a
 * `catch` that puts the message in a banner and swallows the error. A live 500
 * on the 12 Month Analysis report (`division by zero`, unguarded denominator)
 * was hit repeatedly by two operators on preview across 2026-09-16..18 and
 * produced ZERO bug reports, while the server's own onError hook had recorded
 * every one. Hooking the fetch layer closes the class, not just that page.
 *
 * Reported for its own sake, NOT rethrown differently — every existing caller
 * still receives the same `HttpError` it always did.
 */
export function reportHttpFailure(path: string, status: number, message: string): void {
  // 401/403 are expected during sign-in and for low-privilege users — the
  // same exclusion `main.tsx` has always applied. A 404 is excluded too: it is
  // routinely a legitimate answer ("no batch with that id"), and a caller that
  // considers one a fault surfaces it itself.
  //
  // 409 joins them: it is the optimistic-lock conflict every editable surface
  // in this repo is REQUIRED to surface in its own modal
  // (`.claude/rules/database/optimistic-locking.md` — the 409 carries
  // `current_row` so the operator sees a Was/Now diff). All fourteen of the
  // `meta: { silent: true }` sites that used to suppress a report existed for
  // this status.
  if (status === 409) return;
  if (status === 401 || status === 403 || status === 404) return;
  // The PATH without its query string. A report id, a partner id or a period
  // in the querystring would make every occurrence a distinct dedupe key, so
  // one broken endpoint hit with ten parameter sets would file ten reports.
  const endpoint = path.split('?')[0] ?? path;
  autoBugReport('api_error', `${status} ${endpoint}: ${message}`, { status, path: endpoint });
}

/**
 * The same report for a caller that must hold the `Response` itself — a file
 * download needs the blob and the `x-report-truncated` headers, so it cannot
 * go through `apiJson` and is therefore invisible to both automatic paths.
 * Named for what it is rather than exposing the generic one, so a future
 * caller has to say out loud that it is hand-rolling a fetch.
 */
export function reportCsvDownloadFailure(slug: string, status: number, message: string): void {
  reportHttpFailure(`/api/reports/${slug}`, status, message);
}

/**
 * Opt out of the automatic bug report for ONE call, because this caller shows
 * the failure itself and a duplicate report would be noise.
 *
 * ⚠️ Use it only where the caller genuinely renders the error to the operator.
 * The common case does NOT need it: 401/403/404/409 are already excluded in
 * `reportHttpFailure`, which covers every conflict-on-save surface. What is
 * left is the handful of calls whose failure is shown in place and whose
 * status could be anything — a document upload rejected as too large, say.
 */
export interface ApiJsonOptions {
  /** Skip the automatic bug report. The thrown HttpError is unchanged. */
  silent?: boolean;
}

export async function apiJson<T>(
  msal: IPublicClientApplication | null,
  path: string,
  init: RequestInit = {},
  opts: ApiJsonOptions = {},
): Promise<T> {
  const res = await apiFetch(msal, path, init);
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* non-JSON */ }
    const message = describeHttpFailure(res.status, body);
    if (!opts.silent) reportHttpFailure(path, res.status, message);
    throw new HttpError(res.status, message, body);
  }
  return res.json() as Promise<T>;
}
